/**
 * Hero Split Slider — media fallbacks + Splide integration
 *
 * Carousel motion (swipe, arrows, autoplay, keyboard) is Splide,
 * initialized by the theme's makeSlideshow() on `.splide`.
 *
 * This module only:
 *   1. Falls back to poster/placeholder when image or video fails
 *   2. Plays background video on the active slide, pauses the rest
 *   3. Announces slide changes to the live region
 *
 * UMD export keeps the pure helpers available to Node tests.
 */
(function (root, factory) {
  var api = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  root.HeroSplitSliderCore = api;

  if (typeof customElements !== 'undefined' && !customElements.get('hero-split-slider')) {
    api.defineCustomElement();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var DESKTOP_BREAKPOINT = 990;
  var MEDIA_TIMEOUT_MS = 8000;

  /* ------------------------------------------------------------------ */
  /* Pure helpers (unit-tested)                                          */
  /* ------------------------------------------------------------------ */

  function getNextIndex(current, total, delta) {
    if (!total || total < 1) return 0;
    return (current + delta + total) % total;
  }

  function getLayoutMode(width, breakpoint) {
    var bp = breakpoint == null ? DESKTOP_BREAKPOINT : breakpoint;
    return width >= bp ? 'desktop-split' : 'mobile-stack';
  }

  function shouldAutoplay(options) {
    var opts = options || {};
    if (!opts.autoplay) return false;
    if ((opts.slideCount || 0) < 2) return false;
    if (opts.reduceMotion) return false;
    return true;
  }

  function resolveMediaFallback(options) {
    var opts = options || {};
    if (!opts.error) return 'source';
    if (opts.poster) return 'poster';
    return 'placeholder';
  }

  function announceSlide(index, total, template) {
    var current = index + 1;
    var text = template || 'Slide [[current]] of [[total]]';
    return text
      .replace('[[current]]', String(current))
      .replace('[[total]]', String(total))
      .replace('{{ current }}', String(current))
      .replace('{{ total }}', String(total));
  }

  function rectsOverlap(a, b, tolerance) {
    var t = tolerance == null ? 0.5 : tolerance;
    if (!a || !b) return false;
    return a.left < b.right - t && a.right > b.left + t && a.top < b.bottom - t && a.bottom > b.top + t;
  }

  function panesAreSideBySide(leftRect, rightRect, tolerance) {
    var t = tolerance == null ? 0.5 : tolerance;
    return leftRect.right <= rightRect.left + t || rightRect.right <= leftRect.left + t;
  }

  function panesAreStacked(leftRect, rightRect, tolerance) {
    var t = tolerance == null ? 0.5 : tolerance;
    return leftRect.bottom <= rightRect.top + t || rightRect.bottom <= leftRect.top + t;
  }

  /* ------------------------------------------------------------------ */
  /* Media error handling                                                */
  /* ------------------------------------------------------------------ */

  function applyFallback(container, layer) {
    var source =
      container.querySelector('[data-hero-source]') ||
      container.querySelector('video, img.hero-split-slider__background-media, img.hero-split-slider__pane-image');
    var poster = container.querySelector('[data-hero-poster]');
    var placeholder = container.querySelector('[data-hero-fallback]');

    if (source) {
      source.hidden = layer !== 'source';
      source.setAttribute('aria-hidden', layer !== 'source' ? 'true' : 'false');
      if (layer !== 'source' && source.pause) {
        try {
          source.pause();
        } catch (err) {
          /* ignore pause errors on broken media */
        }
      }
    }

    if (poster) {
      poster.hidden = layer !== 'poster';
      poster.setAttribute('aria-hidden', layer !== 'poster' ? 'true' : 'false');
    }

    if (placeholder) {
      placeholder.hidden = layer !== 'placeholder';
      placeholder.setAttribute('aria-hidden', layer !== 'placeholder' ? 'true' : 'false');
    }

    container.setAttribute('data-hero-media-state', layer);
  }

  function bindMediaFallback(container) {
    if (!container || container.getAttribute('data-hero-media-bound') === 'true') return;

    container.setAttribute('data-hero-media-bound', 'true');

    var source =
      container.querySelector('[data-hero-source]') ||
      container.querySelector('video, img.hero-split-slider__background-media, img.hero-split-slider__pane-image');
    var poster = container.querySelector('[data-hero-poster]');
    var timedOut = false;

    function fail() {
      var layer = resolveMediaFallback({
        error: true,
        poster: Boolean(poster),
      });
      applyFallback(container, layer);

      var posterImg = poster && poster.tagName === 'IMG' ? poster : poster && poster.querySelector ? poster.querySelector('img') : null;
      if (layer === 'poster' && posterImg && posterImg.complete === false) {
        posterImg.addEventListener(
          'error',
          function () {
            applyFallback(container, 'placeholder');
          },
          { once: true }
        );
      }
    }

    if (!source) {
      fail();
      return;
    }

    if (!source.hasAttribute('data-hero-source')) {
      source.setAttribute('data-hero-source', '');
    }

    source.addEventListener('error', fail);
    source.addEventListener('abort', fail);

    var sources = source.querySelectorAll ? source.querySelectorAll('source') : [];
    Array.prototype.forEach.call(sources, function (el) {
      el.addEventListener('error', fail);
    });

    if (source.tagName === 'VIDEO') {
      var timer = setTimeout(function () {
        timedOut = true;
        if (source.readyState < 2) fail();
      }, MEDIA_TIMEOUT_MS);

      source.addEventListener(
        'loadeddata',
        function () {
          if (!timedOut) clearTimeout(timer);
        },
        { once: true }
      );
    }

    if (source.tagName === 'IMG' && source.complete && source.naturalWidth === 0 && source.getAttribute('src')) {
      fail();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Custom element — Splide host                                        */
  /* ------------------------------------------------------------------ */

  function defineCustomElement() {
    if (typeof HTMLElement === 'undefined') return;

    class HeroSplitSlider extends HTMLElement {
      connectedCallback() {
        if (this._ready) {
          this.syncMediaPlayback();
          this.attachSplide();
          return;
        }
        this._ready = true;
        this.init();
      }

      disconnectedCallback() {
        this._unbindThemeEditor();
      }

      init() {
        this.slides = Array.prototype.slice.call(this.querySelectorAll('.splide__slide, [data-hero-slide]'));
        this.status = this.querySelector('[data-hero-status]');
        this.liveRegion = this.querySelector('[data-hero-live]');
        this.announceTemplate = this.dataset.announceTemplate || 'Slide [[current]] of [[total]]';
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.current = 0;

        this.querySelectorAll('[data-hero-media]').forEach(bindMediaFallback);
        this.syncMediaPlayback();
        this.attachSplide();
        this._bindThemeEditor();

        var self = this;
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) self.pauseAllVideos();
          else self.syncMediaPlayback();
        });
      }

      /**
       * makeSlideshow() (theme global.bundle.js) mounts Splide on `.splide`.
       * This script is deferred and may run first, so we wait until the
       * instance exists, then hook moved/mounted for video + live region.
       */
      attachSplide() {
        if (!this.classList.contains('splide') || !this.id) return;

        var self = this;
        var tries = 0;

        function connect() {
          var instance = window.slideshows && window.slideshows[self.id];
          if (instance) {
            self._splide = instance;
            instance.on('mounted', function () {
              self.onSlideChange(instance.index, { announce: false });
            });
            instance.on('moved', function (newIndex) {
              self.onSlideChange(newIndex, { user: true });
            });
            instance.on('move', function (newIndex) {
              self.current = newIndex;
              self.syncMediaPlayback();
            });
            self.onSlideChange(instance.index, { announce: false });
            return;
          }

          if (++tries < 120) {
            window.requestAnimationFrame(connect);
          }
        }

        connect();
      }

      onSlideChange(index, options) {
        var opts = options || {};
        var slideEls = this.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
        var total = slideEls.length || 1;
        this.current = index;

        Array.prototype.forEach.call(slideEls, function (slide, i) {
          var active = i === index;
          if (active) slide.removeAttribute('inert');
          else slide.setAttribute('inert', '');
        });

        this.syncMediaPlayback();

        var announcement = announceSlide(index, total, this.announceTemplate);
        if (this.status) this.status.textContent = announcement;
        if (opts.announce !== false && opts.user && this.liveRegion) {
          this.liveRegion.textContent = announcement;
        }
      }

      syncMediaPlayback() {
        var self = this;
        var slides = this.querySelectorAll('.splide__slide');
        if (!slides.length) {
          this.querySelectorAll('video').forEach(function (video) {
            if (self.reduceMotion) video.pause();
            else {
              var playPromise = video.play();
              if (playPromise && playPromise.catch) playPromise.catch(function () {});
            }
          });
          return;
        }

        Array.prototype.forEach.call(slides, function (slide) {
          var active = slide.classList.contains('is-active') && !slide.classList.contains('splide__slide--clone');
          var videos = slide.querySelectorAll('video');
          Array.prototype.forEach.call(videos, function (video) {
            if (active && !self.reduceMotion) {
              var playPromise = video.play();
              if (playPromise && playPromise.catch) {
                playPromise.catch(function () {
                  /* Autoplay can be blocked; poster/fallback stays visible. */
                });
              }
            } else {
              video.pause();
            }
          });
        });
      }

      pauseAllVideos() {
        this.querySelectorAll('video').forEach(function (video) {
          video.pause();
        });
      }

      _bindThemeEditor() {
        var self = this;
        this._onSectionLoad = function (event) {
          if (event.target && event.target.contains(self)) {
            self.attachSplide();
          }
        };
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }

      _unbindThemeEditor() {
        if (this._onSectionLoad) {
          document.removeEventListener('shopify:section:load', this._onSectionLoad);
        }
      }
    }

    customElements.define('hero-split-slider', HeroSplitSlider);
  }

  return {
    DESKTOP_BREAKPOINT: DESKTOP_BREAKPOINT,
    MEDIA_TIMEOUT_MS: MEDIA_TIMEOUT_MS,
    getNextIndex: getNextIndex,
    getLayoutMode: getLayoutMode,
    shouldAutoplay: shouldAutoplay,
    resolveMediaFallback: resolveMediaFallback,
    announceSlide: announceSlide,
    rectsOverlap: rectsOverlap,
    panesAreSideBySide: panesAreSideBySide,
    panesAreStacked: panesAreStacked,
    applyFallback: applyFallback,
    bindMediaFallback: bindMediaFallback,
    defineCustomElement: defineCustomElement,
  };
});
